/**
 * Standardized right-click context menu for drawings. Every drawing renderer
 * builds its menu from `drawingMenuItems` so the entries (Settings + Delete)
 * stay consistent — adding/removing a row is a one-line change here, not per
 * renderer.
 *
 * "Settings" opens the drawing's TOOL settings panel (the same panel the left
 * Toolbar gear + the per-drawing toolbar open) via the shared `ui.settingsToolId`
 * state, so it works from anywhere. Only shown for tools that HAVE a settings
 * panel in the registry (line tools, text, price range — not position/fib, which
 * have their own menus).
 */
import type { AppDispatch } from "@/state/store";
import {
  type DrawingKind,
  removeHorizontalLine,
  removeVerticalLine,
  removeDiagonalLine,
  removePath,
  removeFib,
  removePosition,
  removePriceRangeBox,
  removeTextLabel,
  setSettingsToolId,
} from "@/state/slices/uiSlice";
import { toolById } from "@/tools/_shared/registry";
import type { MenuItem } from "./types";

/** Map a DrawingKind → its registered ToolDef id (null = no tool/settings). */
export function toolIdForKind(kind: DrawingKind): string | null {
  switch (kind) {
    case "hline":
      return "horizontal";
    case "vline":
      return "vertical";
    case "diagonal":
      return "diagonal";
    case "path":
      return "path";
    case "priceRange":
      return "priceRange";
    case "text":
      return "text";
    case "fib": // handled by FibLevelsDialog, not ToolSettings
      return "fib";
    case "position":
      return null;
  }
}

/** Delete a drawing of any kind from a tab. */
export function deleteDrawing(
  dispatch: AppDispatch,
  tabId: string,
  kind: DrawingKind,
  id: string,
): void {
  switch (kind) {
    case "hline":
      dispatch(removeHorizontalLine({ tabId, id }));
      break;
    case "vline":
      dispatch(removeVerticalLine({ tabId, id }));
      break;
    case "diagonal":
      dispatch(removeDiagonalLine({ tabId, id }));
      break;
    case "path":
      dispatch(removePath({ tabId, id }));
      break;
    case "fib":
      dispatch(removeFib({ tabId, id }));
      break;
    case "position":
      dispatch(removePosition({ tabId, id }));
      break;
    case "priceRange":
      dispatch(removePriceRangeBox({ tabId, id }));
      break;
    case "text":
      dispatch(removeTextLabel({ tabId, id }));
      break;
  }
}

/** The standard drawing context menu: Settings (when the tool has a settings
 *  panel) + Delete. `extra` items are appended before Delete (e.g. a tool's own
 *  rows). */
export function drawingMenuItems(
  dispatch: AppDispatch,
  tabId: string,
  kind: DrawingKind,
  id: string,
  extra: MenuItem[] = [],
): MenuItem[] {
  const items: MenuItem[] = [];
  const toolId = toolIdForKind(kind);
  if (toolId && toolById(toolId)?.settings) {
    items.push({
      label: "Settings",
      action: () => dispatch(setSettingsToolId(toolId)),
    });
  }
  items.push(...extra);
  items.push({
    label: "Delete",
    action: () => deleteDrawing(dispatch, tabId, kind, id),
    danger: true,
  });
  return items;
}
